/**
 * ★ 账单导入内核 —— **编码嗅探 / CSV 解析 / 列识别 / 行解析 / 去重三级 / 分类建议**的唯一实现
 * （D 域 · `DR-051` · 批 86）
 *
 * 依据：`D域PRD` **§5.1.1**（编码）· **§5.1.2**（上限）· **§5.1.3**（列识别）· **§5.1.4**（资金事件）·
 * **§5.1.5**（金额与日期）· **§5.2**（去重三级）· **§5.3**（规则四级优先级 / 置信度三档）·
 * **§6.1**（E1–E14）· `AC-D1.2`–`D1.13` · `AC-D2.1`–`D2.8` · `AC-D3.1`–`D3.5` · `AC-D3.11` ·
 * `DR-005`（唯一支出口径 = 两腿不计收支）· `AC-P1.2`（禁浮点）
 *
 * ## 🔴 本文件"拥有"什么（铁律 24）
 *
 * | 判定 | 落在哪 |
 * |---|---|
 * | 编码嗅探与乱码判定 | {@link sniffEncoding} · {@link isGarbled} |
 * | CSV 切分（含引号转义 / CRLF） | {@link parseCsvGrid} |
 * | 列头归一化与模板识别 | {@link detectColumns} |
 * | 金额 / 日期 / 资金事件的**唯一映射** | {@link parseBillRows} |
 * | 商户名归一化 | {@link normalizeMerchant} |
 * | L1 / L2 / L3 去重判据 | {@link classifyDuplicate} |
 * | 分类建议四级 + 置信度三档 | {@link suggestCategory} · {@link confidenceBandOf} |
 * | 账单行 → 腿结构 | {@link legsOfBillRow} |
 *
 * ## 🔴 本文件"不拥有"什么
 *
 * - **不碰库**：入参与返回都是纯数据（`Uint8Array` / 字符串 / 数组）。
 * - **不写流水**：落库经 C 域 `TransactionWriteService.createImported()`（唯一写入出口）。
 * - **不调 AI**：第四级优先级（LLM 兜底）在 E 域落地前**不存在**（`DR-051` D8）。
 *
 * ## 🔴 三个"看起来能做但不能做"的地方
 *
 * 1. **编码判定必须在字节层**：先把 GBK 字节按 UTF-8 解码会把坏字节换成 `U+FFFD`，
 *    那是**不可逆**的损坏 —— 之后再怎么选编码都救不回来。
 * 2. **相似度不做除法**：`0.85` 判据写成 `distance × 100 ≤ 15 × maxLen`（纯乘法），
 *    避开 `AC-P1.2` 对 `/` 的全项目禁令。
 * 3. **置信度用 E4（0–10000）**：与 `AccountPolicy.UsageNoticeE4` 同一标度，
 *    阈值比较全是整数比较（`8500` / `5000`），不引入浮点。
 */

import { fromYmd, parseDay, isDayString, MS_PER_DAY, type Ymd } from './calendar.js';
import { ImportPolicy } from '../constants/index.js';
import { yuanTextToCents } from './money.js';
import type { Cents } from '../types/brand.js';

/**
 * 商户字典的一条（**本类型是唯一声明处**）。
 *
 * 🔴 为什么类型在这里、数据在 `generated/`：`nucleus/**` 是零 IO 的口径内核，
 * `check:arch` 的 `nucleus-no-outward` **不豁免 type-only import** ⇒ 若内核反向 import
 * `generated/`，即便编译后一行代码都不剩，也会被判越界（批 86 实测：2 条 violation）。
 * ⇒ 依赖方向必须反过来：**数据层 import 内核的类型**，内核不认识数据文件。
 */
export interface MerchantDictionaryEntry {
  /** 商户关键词（**中文原文**，匹配时对账单商户名做包含判断） */
  readonly keyword: string;
  /** 命中后建议的分类 id（`cat-*`） */
  readonly categoryId: string;
}

// ═══════════════════════════ ① 编码 ═══════════════════════════

/** 账单编码（P0 三种，`§5.1.1`） */
export const ImportEncoding = {
  Utf8: 'utf-8',
  Gbk: 'gbk',
  Gb18030: 'gb18030',
} as const;
export type ImportEncoding = (typeof ImportEncoding)[keyof typeof ImportEncoding];

export const IMPORT_ENCODINGS: readonly ImportEncoding[] = [
  ImportEncoding.Utf8,
  ImportEncoding.Gbk,
  ImportEncoding.Gb18030,
];

/**
 * `TextDecoder` 的**最小结构类型**。
 *
 * 🔴 为什么不直接用全局 `TextDecoder`：`packages/shared` 的 tsconfig **没有 DOM lib**
 * （它同时被服务端与端侧引用，加 DOM 会把 `window` / `document` 一起放进来）。
 * 而 `TextDecoder` 在 Node 22（full-ICU）与所有现代浏览器里都是**全局可用**的 ⇒
 * 从 `globalThis` 取、并用下面这个结构类型约束，两端都能跑且不污染 lib。
 */
interface DecoderLike {
  decode(input: Uint8Array): string;
}
interface DecoderCtorLike {
  new (label: string, options?: { fatal?: boolean }): DecoderLike;
}

/** 运行期取 `TextDecoder`（两端都有；缺失时**明确抛**而不是静默降级） */
function textDecoderOf(label: string, fatal: boolean): DecoderLike {
  const ctor = (globalThis as unknown as { TextDecoder?: DecoderCtorLike }).TextDecoder;
  if (ctor === undefined) {
    throw new RangeError('当前运行环境没有 TextDecoder，无法解码账单文件');
  }
  return new ctor(label, { fatal });
}

export interface DecodeResult {
  text: string;
  /** 解码后出现的替换字符个数（`U+FFFD`）—— 乱码的**唯一客观指标** */
  replacementChars: number;
  textLength: number;
}

/**
 * 按指定编码解码（**不做 BOM 剥离以外的任何"修复"**）。
 *
 * 🔴 `utf-8` 走 `fatal: true`：坏字节**直接抛**。这是"严格试解码"的前提 ——
 * 否则一段 GBK 文本也会"成功"解出一堆 `U+FFFD`，看起来像"能解析但全是乱码"，
 * 报错信息会指向错误的方向。
 */
export function decodeBytes(bytes: Uint8Array, encoding: ImportEncoding): DecodeResult {
  const decoder = textDecoderOf(encoding, encoding === ImportEncoding.Utf8);
  let text: string;
  try {
    text = decoder.decode(bytes);
  } catch {
    // 严格模式抛错 ⇒ 交给调用方换编码（**不在这里静默降级**）
    return { text: '', replacementChars: Number.POSITIVE_INFINITY, textLength: 0 };
  }
  let replacementChars = 0;
  for (const ch of text) if (ch === '\uFFFD') replacementChars += 1;
  return { text, replacementChars, textLength: text.length };
}

/** BOM 探测（`§5.1.1` 第 ① 步）；返回编码与跳过字节数 */
export function detectBom(bytes: Uint8Array): { encoding: ImportEncoding; skip: number } | null {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { encoding: ImportEncoding.Utf8, skip: 3 };
  }
  // UTF-16 超出 P0 范围（`ImportEncoding` 只有三种）⇒ 只识别、由调用方报"不支持的编码"
  return null;
}

/**
 * 乱码判定：替换字符比例 > `ImportPolicy.MaxReplacementPermille`（千分之五）。
 *
 * 🔴 **用乘法判比例**：`count / len > 5 / 1000` ⟺ `count * 1000 > 5 * len` —— 整数、精确、无除法。
 */
export function isGarbled(replacementChars: number, textLength: number): boolean {
  if (textLength === 0) return replacementChars > 0;
  return replacementChars * 1000 > ImportPolicy.MaxReplacementPermille * textLength;
}

export type SniffResult =
  | { ok: true; encoding: ImportEncoding; hasBom: boolean; text: string; replacementChars: number }
  | { ok: false; reason: string; tried: readonly ImportEncoding[] };

/**
 * 按 `§5.1.1` 的固定顺序嗅探：BOM → UTF-8 严格 → GB18030（GBK 的超集）。
 *
 * 🔴 **为什么要"严格"**：见 {@link decodeBytes} 的说明。UTF-8 严格失败是**强信号**
 * （合法的 UTF-8 文本几乎不可能被误判成 GBK）。
 * 🔴 **GB18030 而不是 GBK**：微信账单实测是 GBK，但 GB18030 是它的**超集**，
 * 用超集解 GBK 文本不会出错，且能多接住"生僻字用 GB18030 编码"的账单。
 */
export function sniffEncoding(bytes: Uint8Array): SniffResult {
  const bom = detectBom(bytes);
  if (bom !== null) {
    const r = decodeBytes(bytes.subarray(bom.skip), bom.encoding);
    if (!isGarbled(r.replacementChars, r.textLength) && r.text !== '') {
      return {
        ok: true,
        encoding: bom.encoding,
        hasBom: true,
        text: r.text,
        replacementChars: r.replacementChars,
      };
    }
  }

  const utf8 = decodeBytes(bytes, ImportEncoding.Utf8);
  if (utf8.text !== '' && !isGarbled(utf8.replacementChars, utf8.textLength)) {
    return {
      ok: true,
      encoding: ImportEncoding.Utf8,
      hasBom: false,
      text: utf8.text,
      replacementChars: utf8.replacementChars,
    };
  }

  const gb = decodeBytes(bytes, ImportEncoding.Gb18030);
  if (gb.text !== '' && !isGarbled(gb.replacementChars, gb.textLength)) {
    return {
      ok: true,
      encoding: ImportEncoding.Gb18030,
      hasBom: false,
      text: gb.text,
      replacementChars: gb.replacementChars,
    };
  }

  return { ok: false, reason: '编码识别失败（三种编码都解出乱码）', tried: IMPORT_ENCODINGS };
}

// ═══════════════════════════ ② CSV ═══════════════════════════

/**
 * CSV → 二维数组（RFC 4180 的最小实现：双引号包裹 + `""` 转义 + CRLF/LF）。
 *
 * 🔴 **必须自己写**：账单里的商户名常带逗号（`星巴克,国贸店`），
 * 用 `split(',')` 会把一行切成两行 —— 而且**行数对不上时没有任何提示**。
 */
export function parseCsvGrid(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\r') {
      // CRLF / 单独 CR 都算换行
      if (text[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += ch;
    }
  }
  // 末行（无结尾换行）
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

// ═══════════════════════════ ③ 列识别 ═══════════════════════════

/** 归一化列头：去空白、全角括号→半角、去符号、小写 */
export function normalizeHeader(text: string): string {
  return text
    .replace(/\s+/g, '')
    .replace(/[（]/g, '(')
    .replace(/[）]/g, ')')
    .replace(/[：:]/g, '')
    .toLowerCase();
}

/**
 * 列名同义词表 —— **唯一来源**。
 *
 * 微信与支付宝的官方列头不同（`交易时间` vs `交易时间`、`金额(元)` vs `金额`），
 * 但归一化后能被同一张表覆盖 ⇒ 不需要两套模板（少一套就少一处漂移）。
 */
const COLUMN_SYNONYMS: Readonly<Record<BillColumn, readonly string[]>> = {
  date: ['交易时间', '交易日期', '时间', '日期', '付款时间', '创建时间'],
  amount: ['金额(元)', '金额', '发生金额', '交易金额', '金额(人民币)'],
  merchant: ['交易对方', '商户名称', '交易对象', '对方', '商户'],
  direction: ['收/支', '收支', '收支类型', '收/付款'],
  status: ['当前状态', '交易状态', '状态'],
  note: ['商品', '商品说明', '商品名称', '备注', '摘要', '交易类型'],
};

export type BillColumn = 'date' | 'amount' | 'merchant' | 'direction' | 'status' | 'note';

/** 列索引映射（缺列时为 `null`） */
export interface ColumnMap {
  date: number;
  amount: number;
  merchant: number;
  direction: number | null;
  status: number | null;
  note: number | null;
  /** 表头所在的网格行下标（微信/支付宝都会在前面写几行说明） */
  headerRowIndex: number;
}

/**
 * 识别列头（`§5.1.3` 前两层）。
 *
 * 🔴 **在整张网格里找表头行**，不假定第 1 行：微信账单前 16 行是"微信支付账单明细"之类的说明，
 * 支付宝也类似。判据 = **必填四列（日期/金额/商户）都能命中**的那一行。
 *
 * 返回 `null` ⇒ 交给调用方报 E2（列出实际列头），**不猜一个默认映射**。
 */
export function detectColumns(grid: readonly (readonly string[])[]): ColumnMap | null {
  const scanLimit = Math.min(grid.length, ImportPolicy.HeaderScanRows);
  for (let r = 0; r < scanLimit; r += 1) {
    const cells = (grid[r] ?? []).map((c) => normalizeHeader(c));
    const find = (col: BillColumn): number | null => {
      const synonyms = COLUMN_SYNONYMS[col].map((s) => normalizeHeader(s));
      // 先精确，再"包含"（如「金额(元)」⊃「金额」）
      const exact = cells.findIndex((c) => c !== '' && synonyms.includes(c));
      if (exact >= 0) return exact;
      const partial = cells.findIndex((c) => c !== '' && synonyms.some((s) => c.includes(s) || s.includes(c)));
      return partial >= 0 ? partial : null;
    };
    const date = find('date');
    const amount = find('amount');
    const merchant = find('merchant');
    if (date !== null && amount !== null && merchant !== null) {
      return {
        date,
        amount,
        merchant,
        direction: find('direction'),
        status: find('status'),
        note: find('note'),
        headerRowIndex: r,
      };
    }
  }
  return null;
}

// ═══════════════════════════ ④ 行解析 ═══════════════════════════

/** 资金事件（`§5.1.4` 的四类，**唯一映射表**） */
export const BillEvent = {
  /** 普通消费 ⇒ 1 腿 `out` */
  Expense: 'expense',
  /** 收入 / 收款 ⇒ 1 腿 `in` */
  Income: 'income',
  /** 「不计收支」/「转账」/「亲属卡」/ 零钱通余额宝 ⇒ **2 腿**，两腿不计收支 */
  Transfer: 'transfer',
  /** 「退款」⇒ 1 腿 `in`（挂支出分类 = 冲减腿） */
  Refund: 'refund',
} as const;
export type BillEvent = (typeof BillEvent)[keyof typeof BillEvent];

export interface BillRow {
  /** 原始网格行号（0 起，与 `parseCsvGrid` 的下标一致）—— 便于用户对照文件 */
  gridRow: number;
  /** `YYYY-MM-DD`（**按交易日期**，`AC-D1.10`） */
  date: string;
  /** 原始时间片段（`HH:mm` 或空）—— 同日排序与互转判定用 */
  timeText: string | null;
  /** 金额（分，**恒为正**；方向由 `direction` 表达） */
  amountCents: Cents;
  direction: 'in' | 'out';
  /** 商户原文 */
  merchant: string;
  /** 归一化商户名（去标点/空格/门店后缀、小写） */
  normalizedMerchant: string;
  event: BillEvent;
  note: string | null;
}

export interface SkippedRow {
  gridRow: number;
  reason: string;
}

/** 日期容错解析（`§5.1.5`）：`YYYY-MM-DD` / `YYYY/MM/DD` / `YYYY年MM月DD日`（可带时间后缀） */
export function parseBillDate(text: string): string | null {
  const cleaned = text.trim();
  if (cleaned === '') return null;
  const m = /(\d{4})\s*[-/年.]\s*(\d{1,2})\s*[-/月.]\s*(\d{1,2})/.exec(cleaned);
  if (m === null) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1900 || y > 2999) return null;
  const day = fromYmd(y, mo, d);
  // 🔴 只认**真实存在**的日期：`fromYmd` 会把 2026-13-01 归一成 2027-01-01（铁律 99）
  const ymd: Ymd = parseDay(day);
  if (ymd.y !== y || ymd.m !== mo || ymd.d !== d) return null;
  return isDayString(day) ? day : null;
}

/** 时间片段（`HH:mm` / `HH:mm:ss`）—— 解析不出来就 `null`（不编一个 `00:00`） */
export function parseBillTime(text: string): string | null {
  const m = /(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(text);
  if (m === null) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh > 23 || mm > 59) return null;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** 金额文本 → 整数分（恒正）；非法返回 `null`（**不猜**） */
export function parseBillAmount(text: string): Cents | null {
  const cleaned = text
    .replace(/[¥￥,，\s元]/g, '')
    .replace(/^[+＋]/, '')
    .trim();
  if (cleaned === '' || cleaned === '-') return null;
  const negative = cleaned.startsWith('-') || cleaned.startsWith('−');
  const digits = negative ? cleaned.slice(1) : cleaned;
  // 🔴 账单金额最多两位小数；三位小数是格式异常（宁可跳过也不要四舍五入吞掉一分钱）
  if (!/^\d+(\.\d{1,2})?$/.test(digits)) return null;
  let cents: number;
  try {
    cents = yuanTextToCents(digits) as number;
  } catch {
    return null;
  }
  return (negative ? -cents : cents) as Cents;
}

/** 「不计收支 / 转账」类关键词（`§5.1.4` 的显式标记 + 平台术语） */
const TRANSFER_HINTS: readonly string[] = ['不计收支', '转账', '亲属卡', '零钱通', '余额宝', '提现', '充值', '转出', '转入'];
/** L2 互转判定的关键词（`§5.2` 原文：提现/转入/充值/零钱/余额） */
const INTERNAL_TRANSFER_HINTS: readonly string[] = ['提现', '转入', '充值', '零钱', '余额'];

export function containsAny(text: string, hints: readonly string[]): boolean {
  return hints.some((h) => text.includes(h));
}

/**
 * 账单行 → {@link BillRow}（`§5.1.4` + `§5.1.5` 的**唯一实现**）。
 *
 * 事件判定**按优先级**（先退款、再转账、最后按方向）：
 * 「退款」与「不计收支」都可能是 `in`，若先按方向判就会把退款当收入 ——
 * 而退款是**冲减**，两者对报表的影响方向相反。
 */
export function parseBillRows(
  grid: readonly (readonly string[])[],
  map: ColumnMap,
): { rows: BillRow[]; skipped: SkippedRow[] } {
  const rows: BillRow[] = [];
  const skipped: SkippedRow[] = [];

  for (let r = map.headerRowIndex + 1; r < grid.length; r += 1) {
    const cells = grid[r] ?? [];
    const cell = (idx: number | null): string => (idx === null ? '' : (cells[idx] ?? '').trim());
    const dateText = cell(map.date);
    const amountText = cell(map.amount);
    const merchant = cell(map.merchant);

    // 全空行（账单文件常有尾部空行）—— 静默跳过，不计入 skipped（它不是"一行数据"）
    if (cells.every((c) => (c ?? '').trim() === '')) continue;

    if (rows.length >= ImportPolicy.MaxRows) {
      skipped.push({ gridRow: r, reason: `超过单文件 ${String(ImportPolicy.MaxRows)} 行上限，后续行未解析` });
      break;
    }

    const date = parseBillDate(dateText);
    if (date === null) {
      skipped.push({ gridRow: r, reason: `日期无法识别：「${dateText}」` });
      continue;
    }
    const amount = parseBillAmount(amountText);
    if (amount === null) {
      skipped.push({ gridRow: r, reason: `金额无法识别：「${amountText}」` });
      continue;
    }
    if (amount === 0) {
      skipped.push({ gridRow: r, reason: '金额为 0，已跳过（E10）' });
      continue;
    }

    const directionText = cell(map.direction);
    const noteText = cell(map.note);
    const statusText = cell(map.status);
    const haystack = `${directionText}|${noteText}|${merchant}|${statusText}`;
    const signed = amount as number;
    const isIncomeText = directionText.includes('收入') || directionText.includes('收');
    const isExpenseText = directionText.includes('支出') || directionText.includes('支');

    // 🔴 顺序：先「退款」再「转账」再按方向 —— 退款与转账都可能是 `in`，
    //    先按方向判会把退款当收入（方向相反的两件事）。
    let event: BillEvent;
    if (haystack.includes('退款')) event = BillEvent.Refund;
    else if (containsAny(haystack, TRANSFER_HINTS)) event = BillEvent.Transfer;
    else if (isIncomeText) event = BillEvent.Income;
    else if (isExpenseText) event = BillEvent.Expense;
    else event = signed < 0 ? BillEvent.Expense : BillEvent.Income;

    // 方向：退款 / 收入 ⇒ `in`；转账 ⇒ the account 是被转出（`out`）还是转入（`in`）；
    // 其余 ⇒ `out`。⚠️ 转账两腿由 `legsOfBillRow()` 给全，这里只记"主腿方向"。
    const direction: 'in' | 'out' =
      event === BillEvent.Income || event === BillEvent.Refund
        ? 'in'
        : event === BillEvent.Transfer
          ? isIncomeText && !isExpenseText
            ? 'in'
            : 'out'
          : 'out';

    rows.push({
      gridRow: r,
      date,
      timeText: parseBillTime(dateText),
      amountCents: Math.abs(signed) as Cents,
      direction,
      merchant,
      normalizedMerchant: normalizeMerchant(merchant),
      event,
      note: noteText === '' ? null : noteText,
    });
  }

  return { rows, skipped };
}

// ═══════════════════════════ ⑤ 商户名归一化 ═══════════════════════════

/**
 * 商户名归一化（`§5.3.3`）：去门店后缀 → 去标点/空格/符号 → 小写。
 *
 * 例：`星巴克(国贸店)` → `星巴克`；`美团-外卖` → `美团外卖`；`LUCKIN COFFEE` → `luckincoffee`
 *
 * 🔴 **先删括号再删标点**：反过来的话括号被删掉，`(国贸店)` 就与商户名黏在一起，
 * 于是"同一家店的不同门店"会被判成不同商户（规则永远学不会）。
 */
export function normalizeMerchant(name: string): string {
  return name
    .replace(/[（(][^）)]*[）)]/g, '')
    .replace(/[\s\p{P}\p{S}]/gu, '')
    .toLowerCase();
}

// ═══════════════════════════ ⑥ 相似度（无除法） ═══════════════════════════

/** Levenshtein 编辑距离（整数，滚动数组） */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min((prev[j] as number) + 1, (cur[j - 1] as number) + 1, (prev[j - 1] as number) + cost);
    }
    prev = cur;
  }
  return prev[b.length] as number;
}

/**
 * 相似度是否 ≥ `percent`（如 `85` 表示 0.85）。
 *
 * 🔴 **纯乘法，不做除法**：`distance / maxLen ≤ (100 - percent) / 100`
 * ⟺ `distance × 100 ≤ (100 - percent) × maxLen`。两边都是整数，判据精确。
 */
export function isSimilarEnough(a: string, b: string, percent: number = ImportPolicy.SimilarityPercent): boolean {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return true;
  return levenshtein(a, b) * 100 <= (100 - percent) * maxLen;
}

// ═══════════════════════════ ⑦ 去重三级 ═══════════════════════════

/** 已有流水的「去重指纹」（由服务层从库里的腿聚合得到，**不是新表**） */
export interface TxnFingerprint {
  date: string;
  amountCents: number;
  direction: 'in' | 'out';
  /** 归一化商户（来自流水备注的归一化结果；没有备注时为 `''`） */
  normalizedMerchant: string;
}

export const DuplicateLevel = {
  /** 日期 + 金额 + 方向**三者全等** ⇒ 静默跳过（`AC-D2.1`） */
  L1: 'L1',
  /** 日期 ±1 天 + 金额全等 + 方向相反 + 一方含互转关键词 ⇒ 标内部互转（`AC-D2.3`） */
  L2: 'L2',
  /** 日期 ±1 天 + 金额全等 + 商户相似度 ≥ 0.85 ⇒ **只标注**（`AC-D2.6`） */
  L3: 'L3',
} as const;
export type DuplicateLevel = (typeof DuplicateLevel)[keyof typeof DuplicateLevel];

export interface DuplicateVerdict {
  level: DuplicateLevel;
  /** 命中的既有流水在入参数组里的下标（便于端侧展示"疑似与哪笔重复"） */
  matchedIndex: number;
}

/** 两个日历日是否相差 ≤ `days` 天（含 0） */
export function withinDays(a: string, b: string, days: number): boolean {
  const pa = parseDay(a);
  const pb = parseDay(b);
  const da = Date.UTC(pa.y, pa.m - 1, pa.d);
  const db = Date.UTC(pb.y, pb.m - 1, pb.d);
  return Math.abs(da - db) <= days * MS_PER_DAY;
}

/**
 * 去重判定（`§5.2` 三级）—— **按 L1 → L2 → L3 顺序**，命中即返回。
 *
 * 🔴 **L3 的语义是"标注"，不是"跳过"**：返回 L3 只意味着"这笔看起来可疑"，
 * 调用方**仍要落库**（`§5.2` 原则：宁可让用户多看一笔，不可静默吞掉一笔真实支出）。
 */
export function classifyDuplicate(
  row: BillRow,
  existing: readonly TxnFingerprint[],
): DuplicateVerdict | null {
  // ── L1：三要素全等 ──
  for (let i = 0; i < existing.length; i += 1) {
    const e = existing[i] as TxnFingerprint;
    if (e.date === row.date && e.amountCents === (row.amountCents as number) && e.direction === row.direction) {
      return { level: DuplicateLevel.L1, matchedIndex: i };
    }
  }
  // ── L2：±1 天 + 金额等 + 方向相反 + 一方含互转关键词 ──
  for (let i = 0; i < existing.length; i += 1) {
    const e = existing[i] as TxnFingerprint;
    if (e.amountCents !== (row.amountCents as number)) continue;
    if (e.direction === row.direction) continue;
    if (!withinDays(e.date, row.date, 1)) continue;
    const hints = containsAny(row.normalizedMerchant, INTERNAL_TRANSFER_HINTS)
      || containsAny(e.normalizedMerchant, INTERNAL_TRANSFER_HINTS);
    if (hints) return { level: DuplicateLevel.L2, matchedIndex: i };
  }
  // ── L3：±1 天 + 金额等 + 商户相似 ──
  for (let i = 0; i < existing.length; i += 1) {
    const e = existing[i] as TxnFingerprint;
    if (e.amountCents !== (row.amountCents as number)) continue;
    if (!withinDays(e.date, row.date, 1)) continue;
    if (e.normalizedMerchant === '' || row.normalizedMerchant === '') continue;
    if (isSimilarEnough(e.normalizedMerchant, row.normalizedMerchant)) {
      return { level: DuplicateLevel.L3, matchedIndex: i };
    }
  }
  return null;
}

// ═══════════════════════════ ⑧ 分类建议（四级 + 三档） ═══════════════════════════

export const SuggestSource = {
  /** 用户显式规则（`import_rules`）—— 置信度 10000 */
  Rule: 'rule',
  /** 历史确认（近 90 天同商户占比 ≥0.8 且 count≥3） */
  History: 'history',
  /** 本地字典（242 条；最长命中 9000 / 单命中 8000） */
  Dictionary: 'dictionary',
  /** 没有建议 ⇒ 未分类（**不是错误**，`AC-D3.11`） */
  None: 'none',
} as const;
export type SuggestSource = (typeof SuggestSource)[keyof typeof SuggestSource];

export interface HistorySuggestEntry {
  normalizedMerchant: string;
  categoryId: string;
  /** 近 90 天该商户的确认笔数 */
  hits: number;
  /** 该商户被归到这个分类的占比（E4） */
  ratioE4: number;
}

export interface SuggestInput {
  normalizedMerchant: string;
  /** 规则表：归一商户名 → 分类 id */
  rules: ReadonlyMap<string, string>;
  /** 历史确认（调用方已按 90 天窗口算好 hits / ratioE4） */
  history: readonly HistorySuggestEntry[];
  /** 字典（默认用共享层的 241 条） */
  dictionary?: readonly MerchantDictionaryEntry[];
}

export interface CategorySuggestion {
  categoryId: string | null;
  /** 置信度（E4：0–10000） */
  confidenceE4: number;
  source: SuggestSource;
  /** 若与已存规则不一致 ⇒ 另一条候选（`AC-D3.5` 的"标注冲突"） */
  conflictCategoryId: string | null;
}

/**
 * 分类建议（`§5.3` 四级优先级）。
 *
 * 🔴 **顺序不可调换**：显式规则（10000）> 历史占比 > 字典（9000/8000）。
 * 用户明确记住的规则**必须**压过统计与字典 —— 否则"我明明教过它"会反复失效。
 */
export function suggestCategory(input: SuggestInput): CategorySuggestion {
  const merchant = input.normalizedMerchant;

  // ① 显式规则
  const ruleHit = merchant === '' ? undefined : input.rules.get(merchant);
  if (ruleHit !== undefined) {
    const dict = dictionaryHit(merchant, input.dictionary);
    return {
      categoryId: ruleHit,
      confidenceE4: 10000,
      source: SuggestSource.Rule,
      // 规则与字典不一致 ⇒ 标冲突（让用户知道"你教过的和内置的不一样"）
      conflictCategoryId: dict !== null && dict !== ruleHit ? dict : null,
    };
  }

  // ② 历史确认（近 90 天 same merchant 占比 ≥0.8 且 count≥3）
  if (merchant !== '') {
    const hist = input.history
      .filter((h) => h.normalizedMerchant === merchant && h.hits >= ImportPolicy.HistoryMinHits)
      .filter((h) => h.ratioE4 >= ImportPolicy.HistoryMinRatioE4)
      .sort((a, b) => (a.ratioE4 === b.ratioE4 ? b.hits - a.hits : b.ratioE4 - a.ratioE4))[0];
    if (hist !== undefined) {
      return {
        categoryId: hist.categoryId,
        confidenceE4: hist.ratioE4,
        source: SuggestSource.History,
        conflictCategoryId: null,
      };
    }
  }

  // ③ 本地字典
  const dict = dictionaryHit(merchant, input.dictionary);
  if (dict !== null) {
    const matches = dictionaryMatches(merchant, input.dictionary);
    return {
      categoryId: dict,
      // 唯一命中 8000；多个命中取最长 ⇒ 9000（`§5.3`「最长命中 / 单命中」）
      confidenceE4: matches.length >= 2 ? ImportPolicy.DictLongestHitE4 : ImportPolicy.DictSingleHitE4,
      source: SuggestSource.Dictionary,
      conflictCategoryId: null,
    };
  }

  // ④ 没有建议 ⇒ 未分类（`AC-D3.11`：未分类不是错误）
  return { categoryId: null, confidenceE4: 0, source: SuggestSource.None, conflictCategoryId: null };
}

/** 命中的全部字典条目（按关键词包含判断） */
export function dictionaryMatches(
  merchant: string,
  dictionary: readonly MerchantDictionaryEntry[] = [],
): MerchantDictionaryEntry[] {
  if (merchant === '') return [];
  return dictionary.filter((d) => merchant.includes(d.keyword.toLowerCase()));
}

/**
 * 取**最长命中**的字典分类。
 *
 * 🔴 `美团外卖` 必须压过 `美团`（更长 = 更具体）。同长时按 `categoryId` 字典序兜底 ——
 * **不按字典数组顺序**（那会让"谁写在前面"变成一条隐性规则，改文件顺序就改行为）。
 */
export function dictionaryHit(
  merchant: string,
  dictionary: readonly MerchantDictionaryEntry[] = [],
): string | null {
  const matches = dictionaryMatches(merchant, dictionary);
  if (matches.length === 0) return null;
  const sorted = [...matches].sort((a, b) => {
    if (a.keyword.length !== b.keyword.length) return b.keyword.length - a.keyword.length;
    return a.categoryId < b.categoryId ? -1 : a.categoryId > b.categoryId ? 1 : 0;
  });
  return (sorted[0] as MerchantDictionaryEntry).categoryId;
}

/** 置信度三档（`§5.3.1`：高 ≥0.85 / 中 0.50–0.85 / 低 <0.50），单位 E4 */
export function confidenceBandOf(confidenceE4: number): 'high' | 'mid' | 'low' {
  if (confidenceE4 >= ImportPolicy.HighBandE4) return 'high';
  if (confidenceE4 >= ImportPolicy.MidBandE4) return 'mid';
  return 'low';
}

// ═══════════════════════════ ⑨ 腿结构 ═══════════════════════════

export interface BillLeg {
  accountId: string;
  direction: 'in' | 'out';
  amount: number;
  categoryId: string | null;
  excludeFromStats: boolean;
}

/**
 * 账单行 → 腿（`§5.1.4` 的唯一实现）。
 *
 * | 事件 | 腿 |
 * |---|---|
 * | `expense` | 1 腿 `out` + 分类 |
 * | `income` | 1 腿 `in` + 分类 |
 * | `refund` | 1 腿 `in` + **支出分类**（= 冲减腿，与手工退款同形） |
 * | `transfer` | **2 腿**（`out` + `in`）**同一账户**、**均不计收支**、**均不挂分类** |
 *
 * 🔴 **转账为什么两条腿落在同一账户**：账单里只看得到一个账户（微信「零钱通转入」不写对方账户）。
 * 若硬造一个"对方账户"，就是**编数据**。两条等额反向腿 ⇒ 账户余额不变、报表不计 ⇒
 * 与 `DR-005`「内部互转」的语义完全一致。
 */
export function legsOfBillRow(row: BillRow, accountId: string, categoryId: string | null): BillLeg[] {
  const amount = row.amountCents as number;
  if (row.event === BillEvent.Transfer) {
    return [
      { accountId, direction: 'out', amount, categoryId: null, excludeFromStats: true },
      { accountId, direction: 'in', amount, categoryId: null, excludeFromStats: true },
    ];
  }
  return [{ accountId, direction: row.direction, amount, categoryId, excludeFromStats: false }];
}

/** 账单行 → 默认流水备注（唯一文案源） */
export function billNoteOf(row: BillRow): string {
  return row.note === null || row.note === '' ? row.merchant : `${row.merchant} · ${row.note}`;
}
